import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { authorizedFetch, scopedKey } from '@/lib/queryClient';
import { useAuth } from '@/auth/AuthProvider';

export interface GalleryItem {
  id: string;
  projectId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  datasetFileId: string | null;
  datasetName: string | null;
  chartType: string | null;
  thumbnailUrl: string | null;
  // Legacy fields for backward compatibility
  plotId?: string;
  preview?: string | null;
}

export interface GalleryResponse {
  success: boolean;
  items: GalleryItem[];
  total: number;
  nextCursor: string | null;
}

export interface UseProjectGalleryOptions {
  projectId: string | null | undefined;
  search?: string;
  sort?: 'recent' | 'title';
  datasetId?: string | null;
  limit?: number;
  enabled?: boolean;
}

export function useProjectGallery(options: UseProjectGalleryOptions) {
  const { projectId, search = '', sort = 'recent', datasetId = null, limit = 20, enabled = true } = options;
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery<GalleryResponse>({
    queryKey: scopedKey(['gallery', projectId, { search, sort, datasetId }]),
    queryFn: async ({ pageParam }) => {
      if (!projectId) throw new Error('projectId is required');
      
      const fetchId = Math.random().toString(36).slice(2, 8);
      const fetchStart = performance.now();
      
      console.log(`[gallery:frontend:${fetchId}] START fetch`, {
        projectId: projectId.slice(0, 8),
        search: search || 'none',
        sort,
        datasetId: datasetId || 'none',
        limit,
        cursor: pageParam ? 'yes' : 'no',
        timestamp: new Date().toISOString(),
      });
      
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (search.trim()) params.set('search', search.trim());
      if (sort) params.set('sort', sort);
      if (datasetId) params.set('datasetId', datasetId);
      if (pageParam) params.set('cursor', pageParam as string);

      const urlStart = performance.now();
      const url = `/api/ai/v2/projects/${projectId}/gallery?${params.toString()}`;
      const urlBuildMs = performance.now() - urlStart;
      
      const networkStart = performance.now();
      const res = await authorizedFetch(url);
      const networkMs = performance.now() - networkStart;
      
      if (!res.ok) {
        const errorText = await res.text().catch(() => 'unknown');
        console.error(`[gallery:frontend:${fetchId}] FETCH FAILED`, {
          status: res.status,
          statusText: res.statusText,
          error: errorText.slice(0, 200),
          networkMs: Math.round(networkMs),
        });
        throw new Error(`Failed to fetch gallery: ${res.status}`);
      }
      
      const parseStart = performance.now();
      const data = await res.json() as GalleryResponse;
      const parseMs = performance.now() - parseStart;
      const totalMs = performance.now() - fetchStart;
      
      console.log(`[gallery:frontend:${fetchId}] FETCH COMPLETE`, {
        itemsCount: data.items?.length || 0,
        total: data.total || 0,
        hasNextCursor: !!data.nextCursor,
        timings: {
          urlBuildMs: Math.round(urlBuildMs),
          networkMs: Math.round(networkMs),
          parseMs: Math.round(parseMs),
          totalMs: Math.round(totalMs),
        },
      });
      
      if (totalMs > 2000) {
        console.warn(`[gallery:frontend:${fetchId}] SLOW FETCH`, {
          totalMs: Math.round(totalMs),
          networkMs: Math.round(networkMs),
          parseMs: Math.round(parseMs),
        });
      }
      
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && !!projectId && !!user,
    initialPageParam: undefined as string | undefined,
  });

  // Flatten all pages into a single array
  const flattenStart = performance.now();
  const items: GalleryItem[] = query.data?.pages.flatMap((page) => page.items) ?? [];
  const flattenMs = performance.now() - flattenStart;
  
  if (flattenMs > 10 && items.length > 0) {
    console.log(`[gallery:frontend] flatten items`, {
      pages: query.data?.pages.length || 0,
      itemsCount: items.length,
      flattenMs: Math.round(flattenMs),
    });
  }

  return {
    items,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    refetch: query.refetch,
    total: query.data?.pages[0]?.total ?? 0,
    invalidate: () => {
      queryClient.invalidateQueries({ queryKey: scopedKey(['gallery', projectId]) });
    },
  };
}

